import { Loader2 } from 'lucide-react';
import type { UserAnalytics } from '../../api/extensions/useAdminOps';

/**
 * Compact analytics row that sits inside the Manage Users page header next
 * to the title and count badge. Two panels — gender + age-bucket — share the
 * row so the cards don't add a separate vertical strip below the header.
 *
 * Charts are drawn as pure SVG donuts (no extra dependency). Each chart pairs
 * with a labelled legend, which is the text equivalent screen readers surface
 * — pie charts alone fail a11y, but the legend gives every value a reachable
 * text representation.
 */

type Slice = {
  key: string;
  label: string;
  value: number;
  color: string;
};

const GENDER_COLORS: Record<keyof UserAnalytics['gender'], string> = {
  male: '#0ea5e9',     // sky-500
  female: '#ec4899',   // pink-500
  unknown: '#9ca3af',  // gray-400
};

const GENDER_LABELS: Record<keyof UserAnalytics['gender'], string> = {
  male: 'Male',
  female: 'Female',
  unknown: 'Unknown',
};

const AGE_COLORS: Record<keyof UserAnalytics['age'], string> = {
  '0-17': '#0ea5e9',   // sky-500
  '18-30': '#10b981',  // emerald-500
  '31-45': '#f59e0b',  // amber-500
  '46-60': '#f97316',  // orange-500
  '60+': '#f43f5e',    // rose-500
  unknown: '#9ca3af',  // gray-400
};

const AGE_LABELS: Record<keyof UserAnalytics['age'], string> = {
  '0-17': '0–17',
  '18-30': '18–30',
  '31-45': '31–45',
  '46-60': '46–60',
  '60+': '60+',
  unknown: 'Unknown',
};

const MEMBER_COLORS: Record<keyof UserAnalytics['memberStatus'], string> = {
  active: '#10b981',   // emerald-500 — active member
  inactive: '#f59e0b', // amber-500 — pending/inactive
  none: '#9ca3af',     // gray-400 — no membership at all
};

const MEMBER_LABELS: Record<keyof UserAnalytics['memberStatus'], string> = {
  active: 'Member',
  inactive: 'Inactive',
  none: 'Non-member',
};

export function UserAnalyticsCards({
  data,
  isLoading,
  leadingDivider = false,
}: {
  data: UserAnalytics | undefined;
  isLoading: boolean;
  // When true, the first (Gender) panel renders with a leading vertical
  // divider at lg+ so it visually separates from a preceding sibling (the
  // page title + count cluster on the Manage Users page). lg-only because
  // that is the breakpoint at which the parent layout switches to a row.
  leadingDivider?: boolean;
}) {
  const genderSlices: Slice[] = data
    ? (Object.keys(GENDER_LABELS) as Array<keyof UserAnalytics['gender']>).map((k) => ({
        key: k,
        label: GENDER_LABELS[k],
        value: data.gender[k],
        color: GENDER_COLORS[k],
      }))
    : [];

  const ageSlices: Slice[] = data
    ? (Object.keys(AGE_LABELS) as Array<keyof UserAnalytics['age']>).map((k) => ({
        key: k,
        label: AGE_LABELS[k],
        value: data.age[k],
        color: AGE_COLORS[k],
      }))
    : [];

  const memberSlices: Slice[] = data
    ? (Object.keys(MEMBER_LABELS) as Array<keyof UserAnalytics['memberStatus']>).map((k) => ({
        key: k,
        label: MEMBER_LABELS[k],
        value: data.memberStatus[k],
        color: MEMBER_COLORS[k],
      }))
    : [];

  const total = data?.total ?? 0;
  const signups = data?.signups;

  // Dividers ride on each panel-after-first via border-l so they wrap with the
  // panel they belong to. A separate <Divider/> element + flex-wrap would
  // strand the divider on the wrong line when the row breaks.
  const trailingPanel = 'sm:pl-6 sm:border-l';
  // Gender's leading divider only kicks in at lg+, matching the breakpoint
  // at which the parent layout puts the title cluster to its left.
  const leadingPanel = leadingDivider ? 'lg:pl-6 lg:border-l' : '';

  return (
    <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 sm:gap-y-2">
      <AnalyticsPanel
        title="Gender"
        slices={genderSlices}
        total={total}
        isLoading={isLoading}
        className={leadingPanel}
      />
      <AnalyticsPanel
        title="Age"
        slices={ageSlices}
        total={total}
        isLoading={isLoading}
        className={trailingPanel}
      />
      <AnalyticsPanel
        title="Membership"
        slices={memberSlices}
        total={total}
        isLoading={isLoading}
        className={trailingPanel}
      />
      <SignupTrendPanel
        signups={signups}
        isLoading={isLoading}
        className={trailingPanel}
      />
    </div>
  );
}

function AnalyticsPanel({
  title,
  slices,
  total,
  isLoading,
  className = '',
}: {
  title: string;
  slices: Slice[];
  total: number;
  isLoading: boolean;
  className?: string;
}) {
  // Every labelled bucket is always rendered in the legend, even at 0, so
  // operators can confirm the bucket boundaries at a glance rather than
  // wondering whether a missing row means "no users" or "category removed".
  // The chart itself still only draws non-zero slices.
  const a11yLabel = `${title} distribution: ${
    slices.length === 0
      ? 'no data'
      : slices.map((s) => `${s.label} ${s.value}`).join(', ')
  }`;

  if (isLoading && total === 0) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <span className="text-xs text-muted-foreground">No users in filter</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Donut slices={slices} total={total} ariaLabel={a11yLabel} />
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
          {title}
        </div>
        <ul className="space-y-0.5">
          {slices.map((s) => {
            const pct = total > 0 ? (s.value / total) * 100 : 0;
            return (
              <li
                key={s.key}
                className="flex items-center gap-1.5 text-xs leading-tight"
              >
                <span
                  className="inline-block h-2 w-2 rounded-sm shrink-0"
                  style={{ backgroundColor: s.color }}
                  aria-hidden
                />
                <span className="text-foreground">{s.label}</span>
                <span className="tabular-nums text-muted-foreground ml-1">
                  {s.value}
                  <span className="ml-1 opacity-70">({pct.toFixed(0)}%)</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * Pure-SVG donut chart. Sums non-zero slices, then walks them around the
 * circle drawing one arc per slice. Total of zero short-circuits to an
 * empty ring so we never divide by zero.
 *
 * Geometry: r = 36, donut thickness 12 (innerR = 24). Center (50, 50) on
 * a 100×100 viewBox so we can scale freely with width/height props.
 */
function Donut({
  slices,
  total,
  ariaLabel,
}: {
  slices: Slice[];
  total: number;
  ariaLabel: string;
}) {
  const r = 36;
  const innerR = 24;
  const cx = 50;
  const cy = 50;

  const nonZero = slices.filter((s) => s.value > 0);

  // Single non-zero slice → a full ring (two-arc workaround, since a single
  // 360° arc renders as a no-op in SVG).
  if (nonZero.length === 1 && total > 0) {
    return (
      <svg
        viewBox="0 0 100 100"
        className="h-16 w-16 shrink-0"
        role="img"
        aria-label={ariaLabel}
      >
        <FullRing cx={cx} cy={cy} r={r} innerR={innerR} fill={nonZero[0].color} />
        <text
          x={cx}
          y={cy + 1}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-foreground"
          style={{ fontSize: 18, fontWeight: 600 }}
        >
          {total}
        </text>
      </svg>
    );
  }

  let cursor = -Math.PI / 2; // start at 12 o'clock

  return (
    <svg
      viewBox="0 0 100 100"
      className="h-16 w-16 shrink-0"
      role="img"
      aria-label={ariaLabel}
    >
      {/* Background ring — visible when total is 0 or as a faint base */}
      <FullRing cx={cx} cy={cy} r={r} innerR={innerR} fill="hsl(var(--muted))" />
      {total > 0 &&
        nonZero.map((s) => {
          const angle = (s.value / total) * 2 * Math.PI;
          const start = cursor;
          const end = cursor + angle;
          cursor = end;
          const largeArc = angle > Math.PI ? 1 : 0;

          const x1 = cx + r * Math.cos(start);
          const y1 = cy + r * Math.sin(start);
          const x2 = cx + r * Math.cos(end);
          const y2 = cy + r * Math.sin(end);
          const x3 = cx + innerR * Math.cos(end);
          const y3 = cy + innerR * Math.sin(end);
          const x4 = cx + innerR * Math.cos(start);
          const y4 = cy + innerR * Math.sin(start);

          const d = [
            `M ${x1} ${y1}`,
            `A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`,
            `L ${x3} ${y3}`,
            `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x4} ${y4}`,
            'Z',
          ].join(' ');

          return <path key={s.key} d={d} fill={s.color} />;
        })}

      {/* Center label — total in the middle of the donut. Larger font since
          the donut is smaller now and the number is the focal point. */}
      <text
        x={cx}
        y={cy + 1}
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-foreground"
        style={{ fontSize: 18, fontWeight: 600 }}
      >
        {total}
      </text>
    </svg>
  );
}

/**
 * Compact bar chart of daily signups across the analytics window (30 days).
 * Each bar is one day; height scales to the busiest day in the window so a
 * single huge day doesn't squash the rest to invisibility.
 *
 * Like the donuts, the chart pairs with an a11y text equivalent — daily
 * counts are conveyed visually but the summary line ("N signups · last X
 * days") gives screen readers the headline number, and the role="img"
 * aria-label on the SVG describes the trajectory.
 */
function SignupTrendPanel({
  signups,
  isLoading,
  className = '',
}: {
  signups: UserAnalytics['signups'] | undefined;
  isLoading: boolean;
  className?: string;
}) {
  if (isLoading && !signups) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Signups
        </span>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!signups || signups.daily.length === 0) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Signups
        </span>
        <span className="text-xs text-muted-foreground">No data</span>
      </div>
    );
  }

  const days = signups.daily;
  const max = days.reduce((acc, d) => (d.count > acc ? d.count : acc), 0);
  const w = 120;
  const h = 36;
  const gap = 1;
  const barW = (w - gap * (days.length - 1)) / days.length;

  const a11yLabel = `Signups by day over the last ${signups.windowDays} days, total ${signups.total}.`;

  // Find peak day for the legend so the user has a concrete reference point.
  const peak = days.reduce<{ date: string; count: number } | null>(
    (acc, d) => (acc && acc.count >= d.count ? acc : d),
    null,
  );

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="h-10"
        style={{ width: w }}
        role="img"
        aria-label={a11yLabel}
        preserveAspectRatio="none"
      >
        {/* Baseline so empty windows still read as a chart, not a void */}
        <line
          x1={0}
          y1={h - 0.5}
          x2={w}
          y2={h - 0.5}
          stroke="hsl(var(--border))"
          strokeWidth={0.5}
        />
        {days.map((d, i) => {
          const barH = max === 0 ? 0 : (d.count / max) * (h - 2);
          const x = i * (barW + gap);
          const y = h - barH;
          // Empty days get a faint stub so the x-axis is implied; non-empty
          // days use the accent emerald that ties this panel to "growth".
          return (
            <rect
              key={d.date}
              x={x}
              y={d.count === 0 ? h - 0.75 : y}
              width={barW}
              height={d.count === 0 ? 0.75 : barH}
              fill={d.count === 0 ? 'hsl(var(--muted))' : '#10b981'}
              rx={0.5}
            />
          );
        })}
      </svg>
      <div className="min-w-0">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-0.5">
          Signups
        </div>
        <ul className="space-y-0.5">
          <li className="flex items-center gap-1.5 text-xs leading-tight">
            <span className="text-foreground tabular-nums">{signups.total}</span>
            <span className="text-muted-foreground">
              last {signups.windowDays}d
            </span>
          </li>
          {peak && peak.count > 0 && (
            <li className="flex items-center gap-1.5 text-xs leading-tight text-muted-foreground">
              <span>Peak</span>
              <span className="tabular-nums">{peak.count}</span>
              <span className="opacity-70">({formatPeakDate(peak.date)})</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/** Renders an ISO yyyy-MM-dd as "MMM D" without pulling in date-fns. */
function formatPeakDate(iso: string): string {
  // Treat as UTC date-only so the panel doesn't shift labels by tz at the
  // boundary; that would make "today" jump by ±1 depending on viewer offset.
  const [y, m, d] = iso.split('-').map((n) => Number.parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function FullRing({
  cx,
  cy,
  r,
  innerR,
  fill,
}: {
  cx: number;
  cy: number;
  r: number;
  innerR: number;
  fill: string;
}) {
  // even-odd fill rule punches the inner circle out of the outer one to
  // produce a ring without two overlapping paths.
  return (
    <path
      fillRule="evenodd"
      d={`M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${r * 2} 0 a ${r} ${r} 0 1 0 ${-r * 2} 0 M ${cx - innerR} ${cy} a ${innerR} ${innerR} 0 1 0 ${innerR * 2} 0 a ${innerR} ${innerR} 0 1 0 ${-innerR * 2} 0`}
      fill={fill}
    />
  );
}
